import { waitForImageLoading } from './delayed-image-loading'

// Cryptocurrency icons mapping utility
interface CoinMapEntry {
  name: string
  symbol: string
  slug: string
  img_url: string
}

type CoinMap = Record<string, CoinMapEntry>

// Simple memoization cache for loadCoinMap
let coinMapCache: Promise<CoinMap> | null = null

/**
 * Load cryptocurrency icons mapping from GitHub
 * Delays loading until after critical path completes
 */
export const loadCoinMap = async (): Promise<CoinMap> => {
  if (coinMapCache) return coinMapCache

  coinMapCache = (async () => {
    // Wait for image loading to be enabled (delays until after critical path)
    await waitForImageLoading()

    try {
      const response = await fetch(
        'https://raw.githubusercontent.com/ErikThiart/cryptocurrency-icons/refs/heads/master/coin_map.json'
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch coin map: ${response.status}`)
      }

      const rawData = await response.json()

      // Convert from numeric keys to slug-based keys
      const coinMapCache: CoinMap = {}
      for (const [, value] of Object.entries(rawData)) {
        if (value && typeof value === 'object' && 'slug' in value) {
          const coinData = value as CoinMapEntry
          coinMapCache[coinData.slug] = coinData
        }
      }
      return coinMapCache as CoinMap
    } catch (error) {
      console.warn('Failed to load cryptocurrency icons mapping:', error)
      coinMapCache = null // Reset cache on error so it can retry
      return {}
    }
  })()

  return coinMapCache
}

/**
 * Mapping from network type + chain ID to coin identifiers for cryptologos.cc
 */
export const networkToCoinKeyMap: Record<string, string> = {
  // BIP32 networks
  'bip32:0': 'bitcoin-btc', // Bitcoin
  'bip32:2': 'litecoin-ltc', // Litecoin
  'bip32:3': 'dogecoin-doge', // Dogecoin
  'bip32:133': 'zcash-zec', // Zcash
  'bip32:145': 'bitcoin-cash-bch', // Bitcoin Cash

  // Monero
  'monero:128': 'monero', // Monero

  // Ripple/XRP
  'memo:144': 'xrp-xrp', // Ripple/XRP
  'solana:501': 'solana', // Solana
  'cardano:1852': 'cardano', // Cardano
  'memo:148': 'stellar', // Stellar

  'bip32:5': 'dash', // Dash

  // Tron
  'tvm:195': 'tron', // Tron
}

/**
 * Get coin key for a network type and chain ID
 */
export const getCoinKeyForNetwork = (networkType: string, chainId: number): string | null => {
  const key = `${networkType}:${chainId}`
  return networkToCoinKeyMap[key] || null
}

/**
 * Get cryptocurrency icon URL for non-EVM networks
 */
export const getCryptoIconUrl = async (
  networkType: string,
  chainId: number
): Promise<string | null> => {
  const coinKey = getCoinKeyForNetwork(networkType, chainId)
  if (!coinKey) {
    return null
  }
  // Use GitHub cryptocurrency icons repository
  // cryptologos.cc blocks hotlinking/referrer access
  try {
    const coinMap = await loadCoinMap()
    // For simple slugs like 'trx', use them directly
    // For compound slugs like 'bitcoin-btc', extract the first part
    const originalSlug = coinKey.includes('-') ? coinKey.replace(/-[a-z]+$/, '') : coinKey
    const coinData = coinMap[originalSlug]

    if (coinData) {
      // Use the img_url from the coin data, but swap 32 for 128 for higher resolution
      return coinData.img_url.replace('/32/', '/128/')
    }
  } catch (error) {
    console.warn(`Failed to get GitHub icon for ${networkType}:${chainId}:`, error)
  }

  return null
}

/**
 * Direct mapping for common cryptocurrencies
 */
const directCoinInfo: Record<string, { symbol: string; name: string }> = {
  'bitcoin-btc': { symbol: 'BTC', name: 'Bitcoin' },
  'litecoin-ltc': { symbol: 'LTC', name: 'Litecoin' },
  'dogecoin-doge': { symbol: 'DOGE', name: 'Dogecoin' },
  'zcash-zec': { symbol: 'ZEC', name: 'Zcash' },
  'bitcoin-cash-bch': { symbol: 'BCH', name: 'Bitcoin Cash' },
  monero: { symbol: 'XMR', name: 'Monero' },
  'xrp-xrp': { symbol: 'XRP', name: 'XRP' },
  stellar: { symbol: 'XLM', name: 'Stellar' },
  trx: { symbol: 'TRX', name: 'Tron' },
  solana: { symbol: 'SOL', name: 'Solana' },
}

/**
 * Get coin symbol/name for a network
 */
export const getCoinInfo = async (
  networkType: string,
  chainId: number
): Promise<{ symbol: string; name: string } | null> => {
  const coinKey = getCoinKeyForNetwork(networkType, chainId)
  if (!coinKey) {
    return null
  }

  // Primary: Use direct mapping
  if (directCoinInfo[coinKey]) {
    return directCoinInfo[coinKey]
  }

  // Fallback: Use GitHub JSON data
  try {
    const coinMap = await loadCoinMap()
    // Convert cryptologos format back to original slug format for lookup
    const originalSlug = coinKey.replace(/-[a-z]+$/, '') // Remove suffix like -btc, -ltc
    const coinData = coinMap[originalSlug]

    if (coinData) {
      return {
        symbol: coinData.symbol,
        name: coinData.name,
      }
    }
  } catch (error) {
    console.warn(`Failed to get coin info for ${networkType}:${chainId}:`, error)
  }

  return null
}
